import React from "react";
import { useNavigate } from "react-router-dom";
import { Home as HomeIcon, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { DEMO_PROPERTIES } from "../data/demo";
import { StatusPill, Eyebrow, Demo } from "../components/Bits";

const aed = (n) => "AED " + Number(n).toLocaleString();

const MarketTag = ({ market }) => {
  const { t } = useLang();
  const map = { fair: ["market_fair", "text-emerald-400"], below: ["market_below", "text-sky-400"], above: ["market_above", "text-gold"] };
  const [k, c] = map[market] || map.fair;
  return <span className={`text-[11px] ${c}`}>{t(`property.${k}`)}</span>;
};

export default function Property() {
  const { t } = useLang();
  const nav = useNavigate();
  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid="property-page">
      <div className="flex items-center gap-2"><StatusPill status="live" /></div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-3">{t("property.title")}</h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t("property.sub")}</p>

      <button onClick={() => nav("/ask", { state: { prompt: t("property.prompt_example") } })} data-testid="property-ask"
        className="mt-6 w-full max-w-3xl text-left glass rounded-2xl p-5 hover:border-gold/40 transition-colors duration-300 flex items-center justify-between gap-4">
        <span className="text-white/70 text-sm">“{t("property.prompt_example")}”</span>
        <span className="shrink-0 w-10 h-10 rounded-xl bg-gold text-ink flex items-center justify-center"><ArrowRight size={18} /></span>
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {DEMO_PROPERTIES.map((p) => (
          <div key={p.id} data-testid={`property-card-${p.id}`} className="rounded-2xl bg-surface/60 border border-white/[0.06] overflow-hidden hover:border-gold/30 hover:-translate-y-1 transition-all duration-300 group">
            <div className="relative h-40 overflow-hidden">
              <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute top-2 right-2"><Demo /></div>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 text-gold"><HomeIcon size={15} /><span className="font-display text-lg text-white">{p.title}</span></div>
              <div className="text-white/50 text-xs mt-1">{p.area} · {p.bedrooms} BR · {p.baths} BA · {p.size} sqft</div>
              <div className="flex items-center justify-between mt-2">
                <div className="font-display text-xl text-gold">{aed(p.price)}</div>
                <MarketTag market={p.market} />
              </div>
              <div className="text-[11px] text-white/40 mt-1">{aed(Math.round(p.price / p.size))}/sqft · {t("property.freshness")}: {p.freshness}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => nav(`/property/${p.id}`)} data-testid={`property-view-${p.id}`} className="flex-1 text-sm text-gold border border-gold/20 rounded-lg py-2 hover:bg-gold/10 transition-colors duration-200">{t("common.view")}</button>
                <button onClick={() => nav("/ask", { state: { prompt: `Tell me about ${p.title} in ${p.area} and show comparable sales` } })} className="flex-1 text-sm text-white/70 border border-white/10 rounded-lg py-2 hover:border-gold/30 transition-colors duration-200">{t("common.compare")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
